import type { Environment } from '../sim/types';
import { noonUtc, solarDeclinationDeg } from '../sim/astronomy';
import { lightShare, noonParMicromol } from '../sim/light';
import { DEG } from '../sim/math';

/**
 * The sun over the aquarium and its light in the water (M34).
 *
 * **The noon sun, all real** (the user's choice, M34): a game day passes in
 * five seconds at 1×, so a sun swept through the hours would strobe. On the
 * map the sun is drawn where it stands at local noon on the day, on the meridian over
 * the aquarium: high in summer and low in winter, south of overhead or north
 * of it as the latitude and the season put it (at Maui Nui it crosses
 * overhead in late May and again in July). It moves only with the seasons,
 * along the year's path drawn beside it, and it never sets but in polar
 * night. The exhibit lights its tank by the sun at the observation clock's
 * hour instead (M95, `sunAtHour`), and draws no disc.
 *
 * **Its strength** is the day's measured sunlight (`Environment.surfaceParMol`)
 * at noon, as a share of full sunlight, so a cloudy month is dimmer. **The
 * water** glows down to where the light really reaches, by the measured
 * clarity and the fading with depth (`lightShare`), gone where 1% is left.
 *
 * Pure arithmetic, with no Three.js, so `tests/sunlight.test.ts` can hold it;
 * `aquariumView.ts` draws it.
 */

/**
 * Where the noon sun stands on the meridian, degrees from the southern
 * horizon over the zenith to the northern: 0 south, 90 overhead, 180 north.
 * Outside 0 to 180 it does not rise. At noon the sun is as far from the
 * zenith as the latitude is from the declination, on the declination's side.
 */
export function meridianDeg(latDeg: number, declinationDeg: number): number {
  return 90 - latDeg + declinationDeg;
}

/**
 * Which way the noon sun stands from the surface's middle, a unit vector in
 * the scene's axes (x east, y up, z south: north is −z, away from the default
 * camera), at the meridian angle in the meridian's plane. The disc is drawn
 * along it on the path's half circle, and the key light shines along it: a
 * directional light comes from its position toward its target, which three.js
 * keeps at the scene's origin, so the light is placed at this direction
 * itself and not at the disc (review M36: placed at the disc, six units above
 * the origin, it came in at 70° when the sun stood at 46°).
 */
export function sunDirection(meridian: number): [number, number, number] {
  const a = meridian * DEG;
  return [0, Math.sin(a), Math.cos(a)];
}

/** The noon sun's path over the year, as meridian angles: the least and the greatest of the 366 days from `epochDay`, held to the sky. */
export function yearOfNoons(latDeg: number, epochDay: number): { fromDeg: number; toDeg: number } {
  let from = Infinity;
  let to = -Infinity;
  // A leap year's days and one more (a definition: every declination of a year).
  for (let d = 0; d < 366; d++) {
    const m = meridianDeg(latDeg, solarDeclinationDeg(noonUtc(epochDay + d)));
    from = Math.min(from, m);
    to = Math.max(to, m);
  }
  return { fromDeg: Math.max(0, from), toDeg: Math.min(180, to) };
}

/**
 * Full sunlight, PAR, µmol photons m⁻² s⁻¹: a high sun in a clear sky, the
 * round figure the key light is drawn at its full strength for. A display
 * anchor (JUDGED), checked: HOT's deck at Station ALOHA reads 2,010 or more in
 * the clearest tenth of its 556 whole days' noon hours (MEASURED, M34; the
 * hindcast's `noonClearestTenth` since review M37, `scripts/hindcast.ts`).
 */
const FULL_SUN_MICROMOL = 2000;

/**
 * The most the key light is drawn at, as a share of full sunlight: a noon
 * sun a few degrees up divides a little light by a small sine (JUDGED, a
 * display bound; no sanctuary's sun comes near it).
 */
const MOST_BEAM = 1.25;

/**
 * The noon sun's beam as a share of full sunlight: the day's light at noon
 * on a level surface (`noonParMicromol`) over the sine of the sun's height,
 * since the renderer lights a surface by that sine itself. Zero when the sun
 * does not rise.
 */
export function beamShare(env: Pick<Environment, 'surfaceParMol' | 'dayLengthHours' | 'noonSunDeg'>): number {
  if (env.noonSunDeg <= 0) return 0;
  const beam = noonParMicromol(env.surfaceParMol, env.dayLengthHours) / Math.sin(env.noonSunDeg * DEG);
  return Math.min(MOST_BEAM, beam / FULL_SUN_MICROMOL);
}

/**
 * How brightly the water is drawn glowing at a depth, m: 1 just under the
 * surface, 0 where 1% of the light is left, by the light's logarithm (the
 * eye reads light by its ratios, so each tenfold dimming takes the same
 * share of the glow; JUDGED, a display choice).
 */
export function glowAt(kdPar: number, depthM: number): number {
  // Two tenfold steps from the surface to 1% (a definition).
  return Math.min(1, Math.max(0, 1 + Math.log10(lightShare(kdPar, depthM)) / 2));
}

/** What the aquarium draws of a day's sun. */
export interface DaySun {
  /** Where the noon sun stands (`meridianDeg`), or null when it does not rise. */
  meridianDeg: number | null;
  /** Its beam's strength (`beamShare`). */
  beam: number;
  /** The water's clarity near the top, m⁻¹, for the glow. */
  kdPar: number;
}

/** A sanctuary's sun on a day, from its latitude and its environment. */
export function daySun(latDeg: number, epochDay: number, env: Environment): DaySun {
  const m = meridianDeg(latDeg, solarDeclinationDeg(noonUtc(epochDay)));
  return { meridianDeg: env.noonSunDeg > 0 ? m : null, beam: beamShare(env), kdPar: env.kdPar };
}

/** What the aquarium draws of the sun at an hour (M95, the observation clock's). */
export interface HourSun {
  /** Which way it stands, a unit vector in the scene's axes (x east, y up, z south), as `sunDirection`'s. */
  direction: [number, number, number];
  elevationDeg: number;
  /**
   * Its beam's share of full sunlight, met square on: the day's noon beam while it is up (a clear sky's, the air's
   * path not reckoned), none below the horizon; the key light's, which the renderer lays on each surface by its
   * angle to the sun itself (M95's review: scaled by the sun's height as well, a level floor was lit by its square).
   */
  beam: number;
  /** The beam's share on a level surface against noon's: the beam times the sun's height's sine over noon's; the light that reaches the floor's depths and the fish's. */
  level: number;
  /** The sky's light, 0 at night to 1 by day (`SKY_DEG`), for the water behind the tank. */
  sky: number;
  kdPar: number;
  /** The moon then (M104), where it is drawn by; none where it is not reckoned. */
  moon?: HourMoon;
}

/** What the aquarium draws of the moon at an hour (M104, the redesign's 6.4: "moonlight by the real phase"). */
export interface HourMoon {
  direction: [number, number, number];
  elevationDeg: number;
  /** Its disc's lit share, 0 new to 1 full. */
  lit: number;
  /** Its light on a level surface against a full moon overhead: its lit share times its height's sine, none below the horizon. */
  level: number;
}

/** The ecliptic's tilt to the equator, degrees (J2000's mean obliquity, 23.4393°, rounded; it moves 0.013° a century). */
const OBLIQUITY_DEG = 23.44;

/**
 * The sun's longitude along the ecliptic on a day, degrees (0 at the March equinox, 90 at the June solstice): from
 * its declination (`solarDeclinationDeg`, at noon UTC), whose sine is the obliquity's sine times the longitude's, the
 * half of the year told by whether the declination rises toward the next day's (M104's review, for the moon).
 */
export function sunLongitudeDeg(epochDay: number): number {
  const [today, tomorrow] = [solarDeclinationDeg(noonUtc(epochDay)), solarDeclinationDeg(noonUtc(epochDay + 1))];
  const lambda = Math.asin(Math.max(-1, Math.min(1, Math.sin(today * DEG) / Math.sin(OBLIQUITY_DEG * DEG)))) / DEG;
  return tomorrow >= today ? lambda : 180 - lambda;
}

/**
 * The hour, local solar time, civil twilight ends on a day, the sun 6° under the horizon (the definition), by the
 * standard triangle; 24 where it never sinks that far, and 12 where it never rises that high.
 */
export function civilDuskHour(latDeg: number, declinationDeg: number): number {
  const [phi, delta] = [latDeg * DEG, declinationDeg * DEG];
  const cosH = (Math.sin(-6 * DEG) - Math.sin(phi) * Math.sin(delta)) / (Math.cos(phi) * Math.cos(delta));
  if (cosH <= -1) return 24;
  if (cosH >= 1) return 12;
  return 12 + Math.acos(cosH) / DEG / 15;
}

/**
 * The moon at an hour of local solar time on a day `ageDays` into its lunation (M104): its lit share by its age in
 * the mean lunation, as `moonLit` gives it; on the ecliptic, its longitude the sun's (`sunLongitudeDeg`) and a whole
 * turn a lunation more (new, with the sun; full, opposite it), its declination and right ascension from that
 * longitude, and its hour angle the sun's less its right ascension's lead on the sun's (M104's review: a declination
 * merely turned with the phase put a quarter moon 23° off). Left out: its 5.1° tilt to the ecliptic, and the mean
 * lunation's wander from the true new moons, up to 0.59 days, about 7° (`astronomy.ts`). Its height and direction
 * then as the sun's, by the same triangle.
 */
export function moonAtHour(latDeg: number, sunLongitude: number, ageDays: number, synodicDays: number, hour: number): HourMoon {
  const phase = (2 * Math.PI * ageDays) / synodicDays;
  const lit = (1 - Math.cos(phase)) / 2;
  const eps = OBLIQUITY_DEG * DEG;
  const ascension = (lambda: number): number => Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda));
  const [sun, moon] = [sunLongitude * DEG, sunLongitude * DEG + phase];
  const h = (hour - 12) * 15 * DEG - (ascension(moon) - ascension(sun));
  const [phi, delta] = [latDeg * DEG, Math.asin(Math.sin(eps) * Math.sin(moon))];
  const east = -Math.cos(delta) * Math.sin(h);
  const north = Math.cos(phi) * Math.sin(delta) - Math.sin(phi) * Math.cos(delta) * Math.cos(h);
  const up = Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(h);
  return { direction: [east, up, -north], elevationDeg: Math.asin(Math.max(-1, Math.min(1, up))) / DEG, lit, level: lit * Math.max(0, up) };
}

/** The sun's height over which the sky goes from night's dark to day's light, degrees: civil twilight's end below the horizon (a definition) to as far above it (JUDGED). */
const SKY_DEG: readonly [number, number] = [-6, 6];

/**
 * The sun at an hour of local solar time (0 to 24, 12 at noon) on a day: its direction from the hour angle, the
 * latitude and the declination (the sun's east, north and up components, spherical astronomy's standard
 * triangle); its beam the day's noon beam (`DaySun.beam`) while it is up, and its light on a level surface that beam
 * scaled by the sine of its height against noon's, as the direct beam on a level surface goes; the game's daily
 * total keeps its half sine (`noonParMicromol`).
 * Polar day and polar night as they are: a sun that never sets lights midnight; one that never rises, nothing.
 */
export function sunAtHour(latDeg: number, declinationDeg: number, noon: DaySun, hour: number): HourSun {
  const h = (hour - 12) * 15 * DEG;
  const [phi, delta] = [latDeg * DEG, declinationDeg * DEG];
  const east = -Math.cos(delta) * Math.sin(h);
  const north = Math.cos(phi) * Math.sin(delta) - Math.sin(phi) * Math.cos(delta) * Math.cos(h);
  const up = Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(h);
  const noonUp = Math.cos(phi - delta);
  const elevationDeg = Math.asin(Math.max(-1, Math.min(1, up))) / DEG;
  const beam = noon.beam > 0 && up > 0 ? noon.beam : 0;
  const level = beam > 0 ? (noon.beam * up) / Math.max(noonUp, 1e-6) : 0;
  const t = Math.min(1, Math.max(0, (elevationDeg - (SKY_DEG[0] as number)) / ((SKY_DEG[1] as number) - (SKY_DEG[0] as number))));
  return { direction: [east, up, -north], elevationDeg, beam, level, sky: t * t * (3 - 2 * t), kdPar: noon.kdPar };
}
